// ─────────────────────────────────────────────────────────────────────────
// SPOT WORKERS PANEL  (System tab)
//
// Durable audit trail of on-demand quant spot workers: which (if any) is
// running right now and for how long, plus a history of past workers with
// why each one ended (idle self-terminate / AWS spot reclaim / manual deploy
// / crash). Data comes from the QuantSpotWorker ledger, refreshed live on each
// GET by a best-effort EC2 reconcile (the beat task is the 3-min safety net).
//
// Mobile-first, dark-default, institutional styling. Auto-polls every 10s.
// ─────────────────────────────────────────────────────────────────────────
import React, { useCallback, useEffect, useState } from 'react';
import { quantService } from '../../../services/quantService';

const Spinner = ({ className = 'w-3.5 h-3.5' }) => (
  <svg className={`animate-spin ${className}`} viewBox="0 0 24 24" fill="none">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.4 0 0 5.4 0 12h4z" />
  </svg>
);

// Termination category → label + badge color.
const CATEGORY_STYLE = {
  self_idle:     { label: 'Idle self-terminate', cls: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300' },
  spot_reclaim:  { label: 'AWS spot reclaim',    cls: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300' },
  manual_deploy: { label: 'Manual (deploy)',     cls: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' },
  crash:         { label: 'Crash',               cls: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300' },
  unknown:       { label: 'Unknown',             cls: 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400' },
};

function fmtDuration(secs) {
  if (secs == null) return '—';
  const s = Math.max(0, Math.floor(secs));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${sec}s`;
  return `${sec}s`;
}

function fmtTime(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

export default function SpotWorkersPanel() {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);

  const fetchWorkers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await quantService.getSpotWorkers(50);
      setData(res.data);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchWorkers();
    const id = setInterval(fetchWorkers, 10000);
    return () => clearInterval(id);
  }, [fetchWorkers]);

  const active  = data?.active || [];
  const history = data?.history || [];

  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-[#0d1117] overflow-hidden">
      {/* Header */}
      <div className="flex items-center gap-2 px-3 md:px-5 py-2.5 border-b border-gray-200 dark:border-gray-800 bg-gray-50/60 dark:bg-gray-800/20">
        <span className="text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400">
          Spot Workers
        </span>
        <span className={`ml-1 text-[11px] ${active.length > 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-gray-500'}`}>
          {active.length > 0
            ? `${active.length} running`
            : 'none running'}
        </span>
        {loading && <span className="text-gray-400 ml-auto"><Spinner /></span>}
        <button
          onClick={fetchWorkers}
          disabled={loading}
          className={`${loading ? '' : 'ml-auto'} px-2.5 py-1 text-[11px] rounded border border-gray-300 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:border-brand-gold disabled:opacity-50`}
        >
          Refresh
        </button>
      </div>

      <div className="px-3 md:px-5 py-4">
        {error && (
          <div className="mb-3 px-3 py-2 text-xs text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/20 rounded">
            {error}
          </div>
        )}

        {/* Current worker(s) */}
        <div className="mb-5">
          <div className="text-[10px] uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-2">
            Currently running
          </div>
          {active.length === 0 ? (
            <div className="text-xs text-gray-500 dark:text-gray-400 py-3 text-center border border-dashed border-gray-200 dark:border-gray-800 rounded">
              No spot worker is running. One launches automatically when a heavy job is dispatched.
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {active.map(w => (
                <div key={w.id} className="rounded border border-emerald-300 dark:border-emerald-800 bg-emerald-50/50 dark:bg-emerald-900/10 px-3 py-2.5">
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-[11px] text-gray-800 dark:text-gray-200">{w.instance_id}</span>
                    <span className="flex items-center gap-1 text-[11px] text-emerald-600 dark:text-emerald-400">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                      {w.last_state || w.status}
                    </span>
                  </div>
                  <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-0.5 text-[11px] text-gray-500 dark:text-gray-400">
                    <span>Uptime <span className="font-semibold text-gray-700 dark:text-gray-200">{fmtDuration(w.uptime_seconds)}</span></span>
                    <span>{w.instance_type || '—'}</span>
                    <span>{w.availability_zone || '—'}</span>
                  </div>
                  <div className="mt-1 text-[10px] text-gray-400 dark:text-gray-500 truncate" title={w.launch_reason}>
                    launched {fmtTime(w.launched_at)} · {w.launch_reason || 'on-demand'}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* History */}
        <div>
          <div className="text-[10px] uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-2">
            Recent history
          </div>
          {history.length === 0 ? (
            <div className="text-xs text-gray-500 dark:text-gray-400 py-3 text-center">
              {data ? 'No past workers recorded yet.' : 'Loading…'}
            </div>
          ) : (
            <div className="overflow-x-auto scrollbar-thin">
              <table className="w-full text-[11px] min-w-[620px]">
                <thead>
                  <tr className="text-[10px] uppercase tracking-wider text-gray-400 dark:text-gray-500 border-b border-gray-200 dark:border-gray-800">
                    <th className="text-left py-1.5 pr-2">Instance</th>
                    <th className="text-left py-1.5 pr-2">Type</th>
                    <th className="text-left py-1.5 pr-2">Launched</th>
                    <th className="text-left py-1.5 pr-2">Duration</th>
                    <th className="text-left py-1.5 pr-2">Ended</th>
                    <th className="text-left py-1.5 pr-2">Reason</th>
                    <th className="text-left py-1.5">Why ended</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map(w => {
                    const cat = CATEGORY_STYLE[w.termination_category] || CATEGORY_STYLE.unknown;
                    return (
                      <tr key={w.id} className="border-b border-gray-100 dark:border-gray-800/60">
                        <td className="py-1.5 pr-2 font-mono text-[10px] text-gray-700 dark:text-gray-300">{w.instance_id}</td>
                        <td className="py-1.5 pr-2 text-gray-500 dark:text-gray-400">{w.instance_type || '—'}</td>
                        <td className="py-1.5 pr-2 text-gray-500 dark:text-gray-400">{fmtTime(w.launched_at)}</td>
                        <td className="py-1.5 pr-2 text-gray-700 dark:text-gray-200">{fmtDuration(w.uptime_seconds)}</td>
                        <td className="py-1.5 pr-2 text-gray-500 dark:text-gray-400">{fmtTime(w.terminated_at)}</td>
                        <td className="py-1.5 pr-2 text-gray-400 dark:text-gray-500 truncate max-w-[140px]" title={w.launch_reason}>{w.launch_reason || '—'}</td>
                        <td className="py-1.5">
                          <span
                            className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${cat.cls}`}
                            title={w.termination_msg || w.termination_code || ''}
                          >
                            {cat.label}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
